import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, FileDown } from "lucide-react";
import { api, errMsg, API } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const TABS = ["Overview", "Eligibility", "Bank Details", "First Installment", "Second Installment", "Attendance", "Documents", "Activity History"];
const ELIG = ["Eligible", "Ineligible", "Error / Deficiency", "Correction Submitted", "Verification Pending", "Verified", "Archived"];
const BANK = ["Available", "Not Available", "Incorrect Details", "Verification Pending", "Verified"];
const INST = ["Paid", "Pending", "Rejected", "Under Process"];
const ATT = ["Submitted", "Not Submitted", "Error", "Verification Pending"];
const DOC_TYPES = ["Aadhaar", "Admission Proof", "Bonafide Certificate", "Bank Passbook", "Attendance", "Other Required Document"];

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm";
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm">
    <span className="text-slate-500">{label}</span><span className="text-right font-medium text-slate-800">{value || "—"}</span>
  </div>
);
const F = ({ label, children }) => (
  <label className="block"><span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>{children}</label>
);

export default function BeneficiaryDetail() {
  const { bid } = useParams();
  const { can } = useAuth();
  const [tab, setTab] = useState("Overview");
  const [b, setB] = useState(null);
  const [history, setHistory] = useState({ status_history: [], activity: [] });
  const [docs, setDocs] = useState([]);
  const [statusForm, setStatusForm] = useState({ new_status: "", reason: "", remark: "" });
  const [bank, setBank] = useState(null);
  const [first, setFirst] = useState(null);
  const [second, setSecond] = useState(null);
  const [att, setAtt] = useState(null);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/beneficiaries/${bid}`);
      setB(data); setBank(data.bank || {}); setFirst(data.first_installment || {}); setSecond(data.second_installment || {}); setAtt(data.attendance || {});
      setStatusForm((s) => ({ ...s, new_status: data.eligibility_status }));
    } catch (e) { toast.error(errMsg(e)); }
  }, [bid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "Activity History") api.get(`/beneficiaries/${bid}/history`).then(({ data }) => setHistory(data)).catch(() => {});
    if (tab === "Documents") api.get(`/beneficiaries/${bid}/documents`).then(({ data }) => setDocs(data)).catch(() => {});
  }, [tab, bid]);

  const save = async (path, body, label) => {
    try { const { data } = await api.put(path, body); setB(data); toast.success(`${label} updated successfully`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const changeStatus = async () => {
    try {
      await api.post(`/beneficiaries/${bid}/status`, statusForm);
      toast.success("Eligibility status changed");
      setStatusForm({ ...statusForm, reason: "", remark: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const upload = async () => {
    if (!file) return toast.error("Please choose a file");
    const fd = new FormData();
    fd.append("doc_type", docType); fd.append("file", file);
    try {
      await api.post(`/beneficiaries/${bid}/documents`, fd);
      toast.success("Document uploaded");
      setFile(null);
      const { data } = await api.get(`/beneficiaries/${bid}/documents`); setDocs(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeDoc = async (id) => {
    try { await api.delete(`/beneficiaries/documents/${id}`); toast.success("Document deleted"); setDocs(docs.filter((d) => d.id !== id)); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openDoc = async (d) => {
    try {
      const res = await api.get(`/beneficiaries/documents/${d.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!b) return <div className="flex h-64 items-center justify-center text-slate-500"><Loader2 className="animate-spin" /></div>;

  const instPanel = (state, setState, which, title) => (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <F label="Status"><select data-testid={`${which}-status`} value={state.status || ""} onChange={(e) => setState({ ...state, status: e.target.value })} className={inputCls}>{INST.map((s) => <option key={s}>{s}</option>)}</select></F>
      <F label="Amount (₹)"><input data-testid={`${which}-amount`} type="number" value={state.amount ?? 0} onChange={(e) => setState({ ...state, amount: Number(e.target.value) })} className={inputCls} /></F>
      <F label="Sanction Date"><input type="date" value={state.sanction_date || ""} onChange={(e) => setState({ ...state, sanction_date: e.target.value })} className={inputCls} /></F>
      <F label="Payment Date"><input type="date" value={state.payment_date || ""} onChange={(e) => setState({ ...state, payment_date: e.target.value })} className={inputCls} /></F>
      <F label="Transaction ID / UTR"><input data-testid={`${which}-utr`} value={state.utr || ""} onChange={(e) => setState({ ...state, utr: e.target.value })} className={inputCls} /></F>
      <F label="Remark"><input value={state.remark || ""} onChange={(e) => setState({ ...state, remark: e.target.value })} className={inputCls} /></F>
      <div className="sm:col-span-2 lg:col-span-3 flex items-center gap-3">
        {can("write") && <button data-testid={`${which}-save`} onClick={() => save(`/beneficiaries/${bid}/installment/${which}`, state, title)} className="rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white">Save {title}</button>}
        <span className="text-xs text-slate-500">Last updated by {state.updated_by || "—"} {state.updated_at ? `on ${new Date(state.updated_at).toLocaleString("en-IN")}` : ""}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="beneficiary-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-xs text-slate-500">{b.beneficiary_id} · {b.application_number}</div>
          <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">{b.full_name}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            <StatusBadge value={b.eligibility_status} testId="detail-eligibility" />
            <StatusBadge value={b.bank_status} /><StatusBadge value={b.first_installment_status} />
            <StatusBadge value={b.second_installment_status} /><StatusBadge value={b.attendance_status} />
          </div>
        </div>
        {can("write") && <Link to={`/beneficiaries/${bid}/edit`} data-testid="detail-edit-btn" className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm">Edit Record</Link>}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t} data-testid={`dtab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm ${tab === t ? "border-b-2 border-[#0f2545] font-semibold text-[#0f2545]" : "text-slate-600"}`}>{t}</button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        {tab === "Overview" && (
          <div className="grid gap-x-10 md:grid-cols-2">
            <div>
              <Row label="Father's / Husband's Name" value={b.guardian_name} /><Row label="Mother's Name" value={b.mother_name} />
              <Row label="Date of Birth" value={b.dob} /><Row label="Gender" value={b.gender} />
              <Row label="Mobile" value={b.mobile} /><Row label="Aadhaar (masked)" value={b.aadhaar} />
              <Row label="Category" value={b.category} />
              <Row label="Application Type" value={b.application_type} />
              <Row label="Email" value={b.email} />
            </div>
            <div>
              <Row label="Academic Year" value={b.academic_year} /><Row label="Institution" value={b.institution} />
              <Row label="Course / Class" value={`${b.course || ""} ${b.class_year || ""}`} />
              <Row label="Last Year %" value={b.last_year_percent} />
              <Row label="Taluka / District" value={`${b.taluka} / ${b.district}`} />
              <Row label="Location Type" value={b.location_type} /><Row label="Address" value={b.address} />
              <Row label="Created" value={`${b.created_by} · ${b.created_at ? new Date(b.created_at).toLocaleDateString("en-IN") : ""}`} />
              <Row label="Last Updated" value={`${b.updated_by} · ${b.updated_at ? new Date(b.updated_at).toLocaleString("en-IN") : ""}`} />
              <Row label="Remark" value={b.remark} />
            </div>
          </div>
        )}

        {tab === "Eligibility" && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              <F label="New Status"><select data-testid="status-select" value={statusForm.new_status} onChange={(e) => setStatusForm({ ...statusForm, new_status: e.target.value })} className={inputCls}>{ELIG.map((s) => <option key={s}>{s}</option>)}</select></F>
              <F label="Reason (required)"><input data-testid="status-reason" value={statusForm.reason} onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })} className={inputCls} placeholder="e.g. Required document not submitted." /></F>
              <F label="Remark"><textarea data-testid="status-remark" value={statusForm.remark} onChange={(e) => setStatusForm({ ...statusForm, remark: e.target.value })} className={inputCls} rows={2} /></F>
              {can("status") ? (
                <button data-testid="status-save" onClick={changeStatus} className="rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white">Change Status</button>
              ) : <p className="text-xs text-rose-600">Your role cannot change eligibility status.</p>}
              <p className="text-xs text-slate-500">Workflow: Eligible → Ineligible → Error / Deficiency → Correction Submitted → Verification Pending → Verified → Eligible</p>
            </div>
            <div>
              <h4 className="mb-2 text-sm font-semibold text-[#0f2545]">Current reason</h4>
              <p className="mb-4 text-sm text-slate-700">{b.eligibility_reason || "—"}</p>
              <button onClick={() => setTab("Activity History")} className="text-sm text-[#0f2545] underline">View full status history</button>
            </div>
          </div>
        )}

        {tab === "Bank Details" && bank && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <F label="Bank Name"><input data-testid="bank-name" value={bank.bank_name || ""} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} className={inputCls} /></F>
            <F label="Account Holder Name"><input value={bank.account_holder_name || ""} onChange={(e) => setBank({ ...bank, account_holder_name: e.target.value })} className={inputCls} /></F>
            <F label="Account Number (masked unless permitted)"><input data-testid="bank-account" value={bank.account_number || ""} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} className={inputCls} /></F>
            <F label="IFSC Code"><input data-testid="bank-ifsc" value={bank.ifsc || ""} onChange={(e) => setBank({ ...bank, ifsc: e.target.value })} className={inputCls} placeholder="MAHB0000123" /></F>
            <F label="Branch"><input value={bank.branch || ""} onChange={(e) => setBank({ ...bank, branch: e.target.value })} className={inputCls} /></F>
            <F label="Bank Verification Status"><select data-testid="bank-status" value={bank.status || ""} onChange={(e) => setBank({ ...bank, status: e.target.value })} className={inputCls}>{BANK.map((s) => <option key={s}>{s}</option>)}</select></F>
            <F label="Bank Remark"><input value={bank.remark || ""} onChange={(e) => setBank({ ...bank, remark: e.target.value })} className={inputCls} /></F>
            {can("write") && <div className="flex items-end"><button data-testid="bank-save" onClick={() => save(`/beneficiaries/${bid}/bank`, bank, "Bank details")} className="rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white">Save Bank Details</button></div>}
          </div>
        )}

        {tab === "First Installment" && first && instPanel(first, setFirst, "first", "First Installment")}
        {tab === "Second Installment" && second && instPanel(second, setSecond, "second", "Second Installment")}

        {tab === "Attendance" && att && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <F label="Attendance Status"><select data-testid="att-status" value={att.status || ""} onChange={(e) => setAtt({ ...att, status: e.target.value })} className={inputCls}>{ATT.map((s) => <option key={s}>{s}</option>)}</select></F>
            <F label="Month"><input data-testid="att-month" value={att.month || ""} onChange={(e) => setAtt({ ...att, month: e.target.value })} className={inputCls} /></F>
            <F label="From Date"><input type="date" value={att.from_date || ""} onChange={(e) => setAtt({ ...att, from_date: e.target.value })} className={inputCls} /></F>
            <F label="To Date"><input type="date" value={att.to_date || ""} onChange={(e) => setAtt({ ...att, to_date: e.target.value })} className={inputCls} /></F>
            <F label="Total Working Days"><input data-testid="att-working" type="number" value={att.working_days ?? 0} onChange={(e) => setAtt({ ...att, working_days: Number(e.target.value) })} className={inputCls} /></F>
            <F label="Present Days"><input data-testid="att-present" type="number" value={att.present_days ?? 0} onChange={(e) => setAtt({ ...att, present_days: Number(e.target.value) })} className={inputCls} /></F>
            <Row label="Absent Days" value={att.absent_days} /><Row label="Attendance %" value={att.percentage} />
            <F label="Remarks"><input value={att.remarks || ""} onChange={(e) => setAtt({ ...att, remarks: e.target.value })} className={inputCls} /></F>
            {can("write") && <div className="flex items-end"><button data-testid="att-save" onClick={() => save(`/beneficiaries/${bid}/attendance`, att, "Attendance")} className="rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white">Save Attendance</button></div>}
          </div>
        )}

        {tab === "Documents" && (
          <div className="space-y-4">
            {can("docs") && (
              <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <F label="Document Type"><select data-testid="doc-type" value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>{DOC_TYPES.map((d) => <option key={d}>{d}</option>)}</select></F>
                <input data-testid="doc-file" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} className="text-sm" />
                <button data-testid="doc-upload" onClick={upload} className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white"><Upload size={15} /> Upload</button>
                <span className="text-xs text-slate-500">PDF, JPG, JPEG, PNG · max 10 MB</span>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600"><tr>{["Type", "File", "Size", "Uploaded By", "Date", ""].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
              <tbody data-testid="doc-list">
                {docs.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-slate-500">No documents uploaded yet.</td></tr> :
                  docs.map((d) => (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="p-2">{d.doc_type}</td><td className="p-2">{d.original_filename}</td>
                      <td className="p-2">{Math.round(d.size / 1024)} KB</td><td className="p-2">{d.uploaded_by}</td>
                      <td className="p-2">{new Date(d.created_at).toLocaleDateString("en-IN")}</td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          <button data-testid={`doc-view-${d.id}`} onClick={() => openDoc(d)} className="rounded p-1 hover:bg-slate-100"><FileDown size={16} /></button>
                          {can("docs") && <button data-testid={`doc-delete-${d.id}`} onClick={() => removeDoc(d.id)} className="rounded p-1 text-rose-600 hover:bg-rose-50"><Trash2 size={16} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Activity History" && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h4 className="mb-3 text-sm font-semibold text-[#0f2545]">Status History</h4>
              <ol className="space-y-3" data-testid="status-history">
                {history.status_history.map((h) => (
                  <li key={`${h.created_at}-${h.new_status}`} className="rounded-lg border border-slate-200 p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge value={h.previous_status || "—"} /> <span className="text-slate-400">→</span> <StatusBadge value={h.new_status} />
                      <span className="ml-auto text-xs text-slate-500">{new Date(h.created_at).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mt-2 text-slate-600">Reason: {h.reason} {h.remark && `· ${h.remark}`}</div>
                    <div className="text-xs text-slate-400">by {h.changed_by}</div>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold text-[#0f2545]">Activity Log</h4>
              <ol className="space-y-2 text-sm" data-testid="activity-log">
                {history.activity.map((a) => (
                  <li key={`${a.created_at}-${a.action}-${a.module}`} className="border-b border-slate-100 pb-2">
                    <span className="font-medium">{a.user}</span> · {a.module} · {a.action}
                    {a.new_value && <span className="text-slate-600"> → {String(a.new_value)}</span>}
                    <div className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString("en-IN")}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
